/**
 * EmptyState — neutral placeholder for empty tables/cards. Lucide icon +
 * NL title + supporting copy + optional CTA, matched to design/ui/empty-states.
 */
import { isValidElement, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { cn } from './cn';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

export interface EmptyStateProps {
  /**
   * Lucide icon component (preferred) — pass `icon={Activity}` not
   * `icon={<Activity />}`. A JSX element is also accepted for backwards
   * compatibility with admin screens that wrote `icon={<Activity size={24} />}`.
   */
  icon?: IconComponent | ReactNode;
  title: string;
  /** Supporting copy under the title. */
  body?: string;
  /** Alias for `body` used by older admin screens. */
  description?: string;
  /** Optional CTA slot — typically a Button or anchor. */
  cta?: ReactNode;
  /** Smaller variant for inline-card empty slots (less padding). */
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  body,
  description,
  cta,
  compact,
  className,
}: EmptyStateProps): JSX.Element {
  const copy = body ?? description;

  let iconNode: ReactNode = null;
  if (icon) {
    if (isValidElement(icon)) {
      iconNode = icon;
    } else if (typeof icon === 'function') {
      const Icon = icon as IconComponent;
      iconNode = <Icon size={20} aria-hidden="true" />;
    }
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-md border border-dashed border-border-subtle bg-surface text-center',
        compact ? 'gap-2 px-4 py-6' : 'gap-3 px-6 py-10',
        className,
      )}
    >
      {iconNode ? (
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-fg-muted">
          {iconNode}
        </span>
      ) : null}
      <h4 className="text-md font-semibold text-fg-primary">{title}</h4>
      {copy ? <p className="max-w-md text-sm text-fg-muted">{copy}</p> : null}
      {cta ? <div className="mt-2">{cta}</div> : null}
    </div>
  );
}
